import { useCallback, useEffect, useRef, useState } from 'react';
import { RoomEvent } from 'livekit-client';

// Chat, emoji reactions and raised hands for a meeting, carried on LiveKit's
// own data channel rather than over Socket.io like every other realtime
// feature in the app.
//
// That is not a stylistic choice: a meeting guest has no Cyclone account, so
// there is no authenticated socket to carry any of this, and the backend has
// no way to authorize them on one. The LiveKit token they were issued at join
// time (createMeetingToken, the only token in the app with canPublishData)
// is the entire proof that they belong in this meeting, and the data channel
// is the one transport that token already covers. It also means none of this
// touches the backend at all - no new socket events, no server-side meeting
// state to keep in sync with the call.
//
// The tradeoff is that nothing here is persisted: messages are delivered to
// whoever is connected at the time, and someone who joins late starts with an
// empty transcript. Raised hands are the exception - they are state, not
// events, so they are re-announced to each new arrival (see below).

// Everything here rides one topic, so anything else that ever publishes on
// this room's data channel is ignored rather than fed to JSON.parse.
const TOPIC = 'cyclone-meet';

const MAX_MESSAGES = 200;
const MAX_MESSAGE_LENGTH = 500;
// Long enough to read, short enough that a flurry doesn't pile up on screen.
const REACTION_LIFETIME_MS = 4500;
// The live caption panel is a rolling window, not an archive - the full
// transcript is persisted server-side and read back from there.
const TRANSCRIPT_SEGMENT_LIMIT = 300;
// Annotations over a shared screen. Strokes are capped because they are never
// garbage-collected on their own - only an explicit clear removes them - and a
// pointer that stops moving should fade rather than hang there forever.
const MAX_STROKES = 400;
const POINTER_LIFETIME_MS = 2500;

const encoder = new TextEncoder();
const decoder = new TextDecoder();

// Live transcription, fed by the backend's transcript bot rather than by
// another participant: the bot joins the meeting as a LiveKit participant and
// publishes on this same topic (see Backend/services/transcriptBot/
// transcriptSession.js's _publishToMeeting), which is the only way its
// captions can reach a guest who has no socket to be pushed to.
const EMPTY_TRANSCRIPT = { status: 'idle', startedBy: null, segments: [], partials: {}, summary: null, summaryStatus: null };

// Folded out of the data handler because it is the one message family with
// real state transitions rather than a single append.
function reduceTranscript(prev, message) {
    if (message.t === 'stt:state') {
        const state = message.state || {};
        // A fresh run wipes the previous one's lines and summary rather than
        // continuing them - a transcription session's identity is its
        // start-to-stop window.
        if (state.status === 'connecting') {
            return { ...EMPTY_TRANSCRIPT, status: 'connecting', startedBy: state.startedBy || null };
        }
        return {
            ...prev,
            status: state.status || 'idle',
            startedBy: state.startedBy || null,
            // recentSegments is how someone who joined mid-transcription gets
            // context; an empty one must not wipe what we already have.
            segments: state.recentSegments && state.recentSegments.length ? state.recentSegments : prev.segments,
            // Nobody is left mid-sentence once the run is over.
            partials: state.status === 'active' ? prev.partials : {}
        };
    }
    if (message.t === 'stt:segment' && message.segment) {
        const partials = { ...prev.partials };
        // The finalized line supersedes whatever provisional text this
        // speaker had showing.
        delete partials[message.segment.userId];
        return { ...prev, segments: [...prev.segments, message.segment].slice(-TRANSCRIPT_SEGMENT_LIMIT), partials };
    }
    if (message.t === 'stt:partial') {
        return { ...prev, partials: { ...prev.partials, [message.userId]: { username: message.username, text: message.text } } };
    }
    if (message.t === 'stt:summaryPending') return { ...prev, summaryStatus: 'pending' };
    if (message.t === 'stt:summary') return { ...prev, summary: message.summary || null, summaryStatus: message.status || 'failed' };
    return prev;
}

const EMPTY_ANNOTATIONS = { strokes: [], pointers: {} };

// Strokes arrive in pieces: the drawer sends a batch of points every few
// frames under one stroke id, so the line appears as it is being drawn rather
// than all at once when the pen lifts.
function reduceAnnotations(prev, message, from) {
    if (message.t === 'ann:stroke' && Array.isArray(message.pts)) {
        const existing = prev.strokes.find((stroke) => stroke.id === message.id);
        if (existing) {
            return {
                ...prev,
                strokes: prev.strokes.map((stroke) => (stroke.id === message.id
                    ? { ...stroke, pts: [...stroke.pts, ...message.pts] }
                    : stroke))
            };
        }
        const stroke = { id: message.id, identity: from.identity, name: from.name, color: message.color, pts: message.pts };
        return { ...prev, strokes: [...prev.strokes, stroke].slice(-MAX_STROKES) };
    }

    if (message.t === 'ann:ptr') {
        return {
            ...prev,
            pointers: { ...prev.pointers, [from.identity]: { x: message.x, y: message.y, name: from.name, color: message.color, at: Date.now() } }
        };
    }

    if (message.t === 'ann:clear') {
        // 'all' is a request, not an authorization: the data channel cannot
        // enforce who sent what, so this is a convenience for the person
        // sharing rather than a permission. Erasing a drawing is also about
        // as low-stakes as a shared action gets.
        if (message.scope === 'all') return { ...prev, strokes: [] };
        return { ...prev, strokes: prev.strokes.filter((stroke) => stroke.identity !== from.identity) };
    }

    return prev;
}

export function useMeetingSignals(room, localIdentity) {
    const [annotations, setAnnotations] = useState(EMPTY_ANNOTATIONS);
    // Together mode: the scene id the host has put everyone in, or null.
    // A scene *id* rather than a URL - the bundler hashes asset filenames, so
    // a URL would stop resolving the moment two people were on different
    // builds (see utils/meetingScenes.js).
    const [sharedScene, setSharedScene] = useState(null);
    const [messages, setMessages] = useState([]);
    const [transcript, setTranscript] = useState(EMPTY_TRANSCRIPT);
    // identity -> true, for everyone currently holding their hand up.
    const [raisedHands, setRaisedHands] = useState({});
    // Transient, for the floating emoji animation - each entry removes itself.
    const [reactions, setReactions] = useState([]);
    // Lets the chat tab show a dot when it isn't the open tab.
    const [unreadCount, setUnreadCount] = useState(0);

    const handRaisedRef = useRef(false);
    const sharedSceneRef = useRef(null);
    sharedSceneRef.current = sharedScene;
    const isHostRef = useRef(false);
    const roomRef = useRef(room);
    roomRef.current = room;

    const publish = useCallback((payload, { reliable = true, to = undefined } = {}) => {
        const activeRoom = roomRef.current;
        if (!activeRoom) return;
        // Every message states who sent it. The receiver prefers the SDK's own
        // resolution and only falls back to this - see handleData for why that
        // fallback has to exist at all.
        const stamped = {
            ...payload,
            from: { id: localIdentity, name: activeRoom.localParticipant.name || localIdentity }
        };
        activeRoom.localParticipant.publishData(
            encoder.encode(JSON.stringify(stamped)),
            // Reactions are sent lossy on purpose: a dropped emoji is
            // invisible, whereas a dropped chat message or hand state is a bug.
            { reliable, topic: TOPIC, destinationIdentities: to ? [to] : undefined }
            // Swallowed rather than surfaced: publishData rejects when the
            // connection is already going down, which is exactly when there is
            // no longer any UI to report it to.
        ).catch(() => {});
    }, [localIdentity]);

    useEffect(() => {
        if (!room) return undefined;

        const handleData = (payload, participant, _kind, topic) => {
            if (topic !== TOPIC) return;
            let message;
            try {
                message = JSON.parse(decoder.decode(payload));
            } catch (error) {
                return; // not ours, or corrupt - nothing sensible to do with it
            }

            // `participant` is the SDK's own resolution of who sent this, and
            // it is authoritative when present - but it is NOT always present.
            // The SDK looks the sender up among the remote participants it
            // knows about, and comes back undefined when it cannot place them;
            // the transcript bot, which publishes data and no tracks at all,
            // reliably lands in that gap. Requiring it here silently dropped
            // every caption - and every chat message, reaction, raised hand
            // and pen stroke from anyone the lookup happened to miss.
            //
            // So each message also carries who sent it, and that is used as
            // the fallback. Self-declared identity is weaker than the SDK's,
            // which is why it is only consulted second; the things it can
            // affect (a name on a chat bubble, a caption's attribution) are
            // also things the sender could already say whatever they liked
            // about.
            const declared = message.from || {};
            const from = participant
                ? { identity: participant.identity, name: participant.name || participant.identity }
                : { identity: declared.id || '', name: declared.name || '' };
            if (!from.identity) return;

            if (message.t === 'chat' && typeof message.text === 'string') {
                setMessages((prev) => [...prev, {
                    // Sender-generated so the sender's own optimistic copy and
                    // everyone else's agree on one id.
                    id: message.id || `${from.identity}-${Date.now()}`,
                    identity: from.identity,
                    name: from.name,
                    text: message.text.slice(0, MAX_MESSAGE_LENGTH),
                    // Stamped on receipt, not from the payload - a remote
                    // clock can be wrong by hours and would scramble the order.
                    at: Date.now()
                }].slice(-MAX_MESSAGES));
                setUnreadCount((count) => count + 1);
                return;
            }

            if (message.t === 'reaction' && typeof message.emoji === 'string') {
                const id = `${from.identity}-${Date.now()}-${Math.random()}`;
                setReactions((prev) => [...prev, { id, identity: from.identity, name: from.name, emoji: message.emoji.slice(0, 8) }]);
                setTimeout(() => setReactions((prev) => prev.filter((r) => r.id !== id)), REACTION_LIFETIME_MS);
                return;
            }

            // Only the transcript bot sends these, and only a bot: identity is
            // believed when it does - every human's token comes from
            // createMeetingToken, which never mints one. (A participant who
            // forged captions could at worst put words on screen in a meeting
            // they are already in and can already type into.)
            if (typeof message.t === 'string' && message.t.startsWith('stt:')) {
                if (!from.identity.startsWith('bot:')) return;
                setTranscript((prev) => reduceTranscript(prev, message));
                return;
            }

            // Annotations over whatever is being screen-shared. Coordinates
            // are normalized to the video's own content box by the sender
            // (see videoContentRect.js), so they land in the same place on
            // every screen regardless of window size or letterboxing.
            if (typeof message.t === 'string' && message.t.startsWith('ann:')) {
                setAnnotations((prev) => reduceAnnotations(prev, message, from));
                return;
            }

            if (message.t === 'scene') {
                setSharedScene(message.scene || null);
                return;
            }

            if (message.t === 'hand') {
                setRaisedHands((prev) => {
                    const next = { ...prev };
                    if (message.raised) next[from.identity] = true;
                    else delete next[from.identity];
                    return next;
                });
            }
        };

        // A raised hand is state, not an event, so a late joiner would
        // otherwise never learn about it - the broadcast that raised it
        // happened before they connected. Re-announce ours directly to each
        // new arrival (not to the whole room, which already knows).
        const handleParticipantConnected = (participant) => {
            if (handRaisedRef.current) publish({ t: 'hand', raised: true }, { to: participant.identity });
            // Same reasoning as a raised hand: together mode is state, not an
            // event, so someone arriving after it was switched on would
            // otherwise be the one person not in the scene. Only the host
            // re-announces - everyone else is just mirroring it.
            if (sharedSceneRef.current && isHostRef.current) {
                publish({ t: 'scene', scene: sharedSceneRef.current }, { to: participant.identity });
            }
        };

        // Their hand can't still be up if they're gone - and nothing else
        // would ever clear it, since a disconnect sends no 'hand' message.
        const handleParticipantDisconnected = (participant) => {
            setRaisedHands((prev) => {
                if (!prev[participant.identity]) return prev;
                const next = { ...prev };
                delete next[participant.identity];
                return next;
            });
        };

        room.on(RoomEvent.DataReceived, handleData);
        room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
        room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
        return () => {
            room.off(RoomEvent.DataReceived, handleData);
            room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);
            room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
        };
    }, [room, publish]);

    // publishData does not loop back to the sender, so every send below also
    // applies the change locally - otherwise you would never see your own
    // message, your own emoji, or your own raised hand.
    const sendMessage = useCallback((text) => {
        const trimmed = (text || '').trim().slice(0, MAX_MESSAGE_LENGTH);
        if (!trimmed || !roomRef.current) return;
        const id = `${localIdentity}-${Date.now()}`;
        const name = roomRef.current.localParticipant.name || localIdentity;
        publish({ t: 'chat', id, text: trimmed });
        setMessages((prev) => [...prev, { id, identity: localIdentity, name, text: trimmed, at: Date.now(), isLocal: true }].slice(-MAX_MESSAGES));
    }, [publish, localIdentity]);

    const sendReaction = useCallback((emoji) => {
        if (!roomRef.current) return;
        publish({ t: 'reaction', emoji }, { reliable: false });
        const id = `${localIdentity}-${Date.now()}-${Math.random()}`;
        const name = roomRef.current.localParticipant.name || localIdentity;
        setReactions((prev) => [...prev, { id, identity: localIdentity, name, emoji }]);
        setTimeout(() => setReactions((prev) => prev.filter((r) => r.id !== id)), REACTION_LIFETIME_MS);
    }, [publish, localIdentity]);

    const toggleHand = useCallback(() => {
        const raised = !handRaisedRef.current;
        handRaisedRef.current = raised;
        publish({ t: 'hand', raised });
        setRaisedHands((prev) => {
            const next = { ...prev };
            if (raised) next[localIdentity] = true;
            else delete next[localIdentity];
            return next;
        });
    }, [publish, localIdentity]);

    const markChatRead = useCallback(() => setUnreadCount(0), []);

    // Host-only in the UI; like every other data-channel message this is a
    // request rather than an enforced permission, and the worst a participant
    // could do by forging one is change everybody's wallpaper.
    const shareScene = useCallback((sceneId) => {
        isHostRef.current = true;
        publish({ t: 'scene', scene: sceneId || null });
        setSharedScene(sceneId || null);
    }, [publish]);

    // ===== Annotations =====
    //
    // Senders mirror their own output locally for the same reason chat does:
    // publishData never loops back, so without this you would not see your own
    // pen. `color` travels with each message rather than being derived by the
    // receiver, so everyone draws the sender in the one colour their tile
    // already uses.
    const sendStroke = useCallback((id, color, pts) => {
        if (!pts.length) return;
        publish({ t: 'ann:stroke', id, color, pts });
        setAnnotations((prev) => reduceAnnotations(prev, { t: 'ann:stroke', id, color, pts }, { identity: localIdentity, name: 'me' }));
    }, [publish, localIdentity]);

    const sendPointer = useCallback((x, y, color) => {
        // Lossy: a pointer is replaced by the next sample a few frames later,
        // so a dropped one is invisible - and these are the highest-rate
        // messages in the whole protocol.
        publish({ t: 'ann:ptr', x, y, color }, { reliable: false });
    }, [publish]);

    const clearAnnotations = useCallback((scope = 'mine') => {
        publish({ t: 'ann:clear', scope });
        setAnnotations((prev) => reduceAnnotations(prev, { t: 'ann:clear', scope }, { identity: localIdentity, name: 'me' }));
    }, [publish, localIdentity]);

    // Pointers are the one piece of annotation state with no end-of-life
    // message: someone who stops moving the mouse, or leaves, simply stops
    // sending. Expiring them on a timer is what keeps a dot from being
    // stranded on the shared screen.
    useEffect(() => {
        if (Object.keys(annotations.pointers).length === 0) return undefined;
        const interval = setInterval(() => {
            const cutoff = Date.now() - POINTER_LIFETIME_MS;
            setAnnotations((prev) => {
                const live = Object.entries(prev.pointers).filter(([, pointer]) => pointer.at > cutoff);
                if (live.length === Object.keys(prev.pointers).length) return prev;
                return { ...prev, pointers: Object.fromEntries(live) };
            });
        }, 500);
        return () => clearInterval(interval);
    }, [annotations.pointers]);

    return {
        messages, sendMessage, unreadCount, markChatRead,
        reactions, sendReaction,
        raisedHands, handRaised: !!raisedHands[localIdentity], toggleHand,
        transcript,
        annotations, sendStroke, sendPointer, clearAnnotations,
        sharedScene, shareScene
    };
}
